import React, { useState } from 'react';
import { useAuth } from '../../services/contexts/authContext';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input } from '@material-tailwind/react';
import { Lock } from 'lucide-react';
import { STATIC_USER_ROLES } from '../../utils/enums';
import NexusLogo from '../../assets/nexus-logo-horizontal.svg';
import PrivacyPolicyModal from './privacyPolicyModal';
import TermsOfServiceModal from './termsOfServiceModal';
import { URLS } from '../../router-x/routes';

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const resp = await login(email, password);
      if (resp) {
        const user = resp.data.user;
        if (user.role === STATIC_USER_ROLES.USER) {
          navigate(URLS.CUSTOMER.HOME);
        }
        if ([
          STATIC_USER_ROLES.ADMIN,
          STATIC_USER_ROLES.MANAGER,
          STATIC_USER_ROLES.EMPLOYEE
        ].includes(user.role)) {
          navigate(URLS.ADMIN.HOME); // Navigate to dashboard after successful login
        }
        if (user.role === STATIC_USER_ROLES.TEAM_MEMBER) {
          navigate(URLS.TEAM_MEMBER.HOME);
        }
        if (user.role === STATIC_USER_ROLES.RECEPTIONIST) {
          navigate(URLS.RECEPTIONIST.HOME);
        }
      }
      else {
        setError('Failed to login. Try again.');
      }
    } catch (error) {
      setError(
        error.code === 'auth/user-not-found' ? 'User not found' :
          error.code === 'auth/wrong-password' ? 'Incorrect password' :
            'Failed to login. Please check your credentials.'
      );
      console.error('Login error:', error);
    }

    setLoading(false);
  };

  const openPrivacyModal = () => {
    setIsPrivacyModalOpen(true);
  };

  const closePrivacyModal = () => {
    setIsPrivacyModalOpen(false);
  };

  const openTermsModal = () => {
    setIsTermsModalOpen(true);
  };

  const closeTermsModal = () => {
    setIsTermsModalOpen(false);
  };

  // const BG_URL = 'https://firebasestorage.googleapis.com/v0/b/wb-bas.firebasestorage.app/o/resources%2Fbackground-image.jpg?alt=media&token=b60d6df0-6062-4dd2-974b-93f3847f95fd'

  return (
    <div
      className={`min-h-screen flex items-center justify-center bg-cover bg-center p-4 bg-green-800`}
    // style={{ backgroundImage: `url(${BG_URL})` }}
    >
      <div className="w-full bg-white border border-orange-200 max-w-md rounded-3xl p-8">
        <div className="flex flex-col items-center">
          <div className="mb-4">
            <Lock className="text-white" />
          </div>

          <h1 className="text-2xl text-center font-semibold mb-6">
            <div className="flex items-center justify-center mb-2">
              <img src={NexusLogo} alt='Nexus' className='h-20 w-auto max-w-[380px]' />
            </div>
            <span className="text-blue-gray-700 text-lg font-normal">Connecting every corner of your space.</span>
          </h1>

          {error && (
            <Alert severity="error" className="w-full mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin} className="w-full space-y-4">
            <Input
              variant='standard'
              required
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Input
              variant='standard'
              required
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            {/* <div className="flex items-center justify-between">
                <Checkbox
                  value="remember"
                  color="primary"
                  label="Remember me"
                  className="text-gray-700"
                />
              <Link href="#" className="text-sm">
                Forgot password?
                </Link>
              </div> */}

            <Button
              type="submit"
              fullWidth
              variant="gradient"
              disabled={loading}
              loading={loading}
              className="mt-4 py-2 rounded-full"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
        </div>
        <hr className="my-4" />
        <div className="flex items-center justify-center">
          <p className="text-gray-500 text-xs text-center">
            We respect your privacy. Your information is kept secure and used only to improve your experience.
            By continuing, you accept our <button onClick={openPrivacyModal} className="text-orange-500 hover:underline cursor-pointer">Privacy Policy</button> and <button onClick={openTermsModal} className="text-orange-500 hover:underline cursor-pointer">Terms of Service</button>.
          </p>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={closePrivacyModal}
      />

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={closeTermsModal}
      />
    </div>
  );
};

export default Auth;